import { useEffect, useMemo } from 'react'

import {
  hasModel,
  isModelCacheSupported,
  MODEL_CACHE_MAX_ENTRIES,
  putModel,
} from 'Pods/ThreeDStudio/Services/modelCache'
import { isDesktopBreakpoint } from 'Pods/ThreeDStudio/Shared/Utils/isDesktopBreakpoint'
import { isTextureModel } from 'Pods/ThreeDStudio/Shared/Utils/isTextureModel'
import { threeDStudioStore, useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'
import useBreakpoint from 'Utils/useBreakpoint'

// Downloads in progress, so a remount or a second variations tab cannot start a
// duplicate 60 MB fetch of the same model. Only in-flight work lives here -
// whether a model is already stored is Cache Storage's own answer, which is what
// makes the prefetch survive a page reload.
const inFlight = new Set()

// The model files worth fetching ahead: sibling texture variations that are not
// already on screen and still have an output model. Capped at what the cache can
// hold - queueing more would download models that eviction drops before anyone
// switches to them, which on production sizes is tens of megabytes wasted per
// tab open.
const buildPrefetchUrls = (variations, openedModelId) => variations
  .filter(variation => (
    String(variation.id) !== String(openedModelId)
    && isTextureModel(variation.providerModelId)
  ))
  .map(variation => variation.outputAssets?.[0]?.fileUrl)
  .filter(Boolean)
  .slice(0, MODEL_CACHE_MAX_ENTRIES)

// Never race the model the user is waiting on. Switching variations starts a
// load and re-plans the prefetch in the same tick, so without this gate the
// model that just left the viewer would be pulled down over the connection the
// incoming one needs, making the visible load slower than before the prefetch
// existed.
const whenViewerIdle = (signal) => {
  const viewer = threeDStudioStore.getState().staticViewer?.instance
  if (!viewer || viewer.isDisposed || !viewer.isLoading?.()) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const unsubscribe = viewer.subscribe('models', (state) => {
      if (!state.isLoading) {
        unsubscribe()
        resolve()
      }
    })

    signal.addEventListener('abort', () => {
      unsubscribe()
      resolve()
    }, { once: true })
  })
}

// Streams straight into Cache Storage rather than this tab's heap. It goes there
// instead of relying on the HTTP cache because the browser refuses to keep an
// entry this large - see the note in Services/modelCache.
const cacheModel = (url, signal) => fetch(url, { signal }).then((response) => {
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`)
  }

  return putModel(url, response)
})

// Skips whatever is already stored or already downloading, then stores the rest.
// A failure stores nothing, so the next tab open simply tries again.
const prefetchOne = (url, signal) => {
  if (signal.aborted || inFlight.has(url)) {
    return Promise.resolve()
  }

  return hasModel(url).then((stored) => {
    if (stored || signal.aborted || inFlight.has(url)) {
      return undefined
    }

    inFlight.add(url)

    return cacheModel(url, signal)
      .catch(() => {})
      .finally(() => inFlight.delete(url))
  })
}

/**
 * Stores the sibling texture variations' model files in Cache Storage while the
 * variations tab is open, so switching between them skips the download and leaves
 * only the parse and GPU upload. The hook mounts with the tab, so closing it
 * aborts whatever is still in flight.
 *
 * Restricted to texture variations because those are the runs that retexture a
 * shared base mesh - the switch a user actually repeats, and to viewports of md
 * and up. Downloads are chained rather than fired together, and each one waits
 * for the viewer to go idle, so the prefetch never competes with the model on
 * screen.
 */
const usePrefetchVariationModels = (variations) => {
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.id ?? null)
  const breakpoint = useBreakpoint()
  // Nothing to gain where the copies cannot be kept: without Cache Storage the
  // downloads would complete and then be discarded, leaving those browsers paying
  // the bandwidth for nothing.
  const urls = useMemo(() => (
    isDesktopBreakpoint(breakpoint) && isModelCacheSupported()
      ? buildPrefetchUrls(variations, openedModelId)
      : EMPTY_ARRAY
  ), [variations, openedModelId, breakpoint])

  useEffect(() => {
    if (urls.length === 0) {
      return undefined
    }

    const controller = new AbortController()

    urls.reduce((chain, url) => chain
      .then(() => whenViewerIdle(controller.signal))
      .then(() => prefetchOne(url, controller.signal)), Promise.resolve())

    return () => controller.abort()
  }, [urls])
}

export default usePrefetchVariationModels
